import { StepLinearProgress, StepLinearProgressStep } from '@if/mui'
import { Box, Stack, Typography } from '@mui/material'
import {
  APPROVAL_LIKELIHOOD_TIERS,
  ApprovalLikelihood,
  CONVERSION_MODEL,
  conversionProbability,
} from '@src/data/approvalLikelihood'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

const COLORS: Record<ApprovalLikelihood, 'error' | 'warning' | 'info' | 'success'> = {
  marginal: 'error',
  low: 'warning',
  moderate: 'warning',
  good: 'info',
  high: 'info',
  optimal: 'success',
  veryHigh: 'success',
}

const STEPS: StepLinearProgressStep[] = APPROVAL_LIKELIHOOD_TIERS.map(({ threshold, level }) => ({
  from: conversionProbability(threshold),
  color: COLORS[level],
}))

export interface ApprovalLikelihoodBarProps {
  /** Small uppercase heading shown above the bar, e.g. "Customer approval likelihood". */
  label: ReactNode
  likelihood: ApprovalLikelihood
  probability: number
  /** Optional guidance shown under the bar, e.g. how to improve the likelihood. */
  hint?: ReactNode
}

const ApprovalLikelihoodBar = ({ label, likelihood, probability, hint }: ApprovalLikelihoodBarProps) => {
  const { t } = useTranslation()

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1}>
        <Typography variant="overline" color="primary" lineHeight={1.5} component="div">
          {label}
        </Typography>
        <Typography variant="subtitle2" color={`${COLORS[likelihood]}.main`}>
          {t(`common.likelihood.${likelihood}`)}
        </Typography>
      </Stack>
      <StepLinearProgress value={probability} min={CONVERSION_MODEL.P_MIN} max={CONVERSION_MODEL.P_MAX} steps={STEPS} />
      {hint != null && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
          {hint}
        </Typography>
      )}
    </Box>
  )
}

export default ApprovalLikelihoodBar
